import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import { mortgageLabel } from "../../../../../../../../data/buildingPermit/fieldDatas";
import { fieldsToCheckMortgage } from "../../../../../../../../data/permitFormsData";

const MORTGAGE_FIELD_NAMES = ["mortgage_name", "mortgage_address"];

const RenderMortgage = ({ full_title, mortgage, onChange, fields }) => {
  const hasValidatedOnce = useRef(false);

  const savedProjects = useSelector((state) => state.nocData?.saved_projects);

  const { createDocLoading, addingNewMortgage } = useSelector(
    (state) => state.buildingState,
  );

  const [mortgageList, setMortgageList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    setMortgageList(savedProjects?.mortgageLender || []);
    setLoadingData(false);
  }, [savedProjects?.mortgageLender]);

  // Index fields by name once per render — O(1) lookups below
  const fieldMap = useMemo(
    () => Object.fromEntries((fields || []).map((f) => [f.name, f])),
    [fields],
  );

  useEffect(() => {
    // Run only if we haven’t validated AND projectList has loaded
    if (!hasValidatedOnce.current && mortgageList.length > 0 && mortgage) {
      const selectedId = mortgage?.value;
      const selected = mortgageList.find((pro) => pro.id === selectedId);

      if (!selected) {
        onChange("mortgageLender", "");
        MORTGAGE_FIELD_NAMES.forEach((name) => onChange(name, ""));
      }

      hasValidatedOnce.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgageList, mortgage]);

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;
    const selected = mortgageList.find((p) => p.id === selectedId);

    onChange("mortgageLender", {
      value: selectedId,
      label: selected?.["mortgage_name"],
    });
    MORTGAGE_FIELD_NAMES.forEach((name) =>
      onChange(name, selected?.[name] || ""),
    );
  };

  const selectedPro = mortgageList?.find((pro) => pro.id === mortgage?.value);

  if (loadingData) {
    return <Skeleton variant="rectangular" height={120} />;
  }

  return (
    <>
      <Typography align="center" mt={2} mb={0} variant="h6">
        {full_title}
      </Typography>

      <FormControl
        fullWidth
        margin="normal"
        variant="filled"
        sx={{
          "& .MuiFilledInput-root": {
            border: mortgage ? "2px solid #004976" : undefined, // adjust thickness and color
            borderRadius: "14px",
            backgroundColor: "transparent", // optional
          },
          "& .MuiFilledInput-underline:before": {
            borderBottom: "none", // remove default bottom border
          },
          "& .MuiFilledInput-underline:after": {
            borderBottom: "none",
          },
        }}
      >
        <InputLabel>Select Mortgage Lender</InputLabel>
        <Select
          disabled={createDocLoading || addingNewMortgage}
          name="mortgageLender"
          value={mortgage?.value || ""}
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: "1.5px",
                maxHeight: 200,
                width: "auto",
                maxWidth: "100%",
              },
            },
          }}
        >
          <MenuItem disabled value="">
            {mortgageList.length === 0 ? "No records found" : "Select One"}
          </MenuItem>
          {mortgageList?.map((option, i) => (
            <MenuItem key={option.id + i} value={option.id}>
              {option.mortgage_name || "Unnamed"}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {mortgage && selectedPro && (
        <>
          {fieldsToCheckMortgage?.map((field, i) => (
            <TextField
              key={field + i}
              fullWidth
              margin="normal"
              variant="filled"
              label={mortgageLabel[field]}
              name={field}
              value={
                field === "mortgage_name"
                  ? mortgage?.label
                  : fieldMap[field]?.value || ""
              }
              disabled={!addingNewMortgage}
            />
          ))}
        </>
      )}
    </>
  );
};

export default RenderMortgage;
